import {
  getCoverImageUrl,
  getMediaUrl,
  isVideoMedia,
} from "@src/features/contentDetail/utils/media";
import { getLinkType, LinkType } from "@src/lib/utils/files";
import { ReactElement } from "react";

import { MediaGalleryItem } from "@src/lib/ui/MediaGallery";
import PostMediaTile, { PostMediaTileMedia } from "./PostMediaTile";

export const sortMediaAttachments = <T extends { index: number }>(
  medias: readonly T[],
): T[] => [...medias].sort((a, b) => a.index - b.index);

export type GalleryEntry = {
  item: MediaGalleryItem;
  attachmentId: PostMediaTileMedia["id"];
  title?: string;
  libraryItemId?: string;
  pdfUrl?: string;
};

export const toGalleryEntries = (
  medias: readonly PostMediaTileMedia[],
): GalleryEntry[] => toOrderedGalleryEntries(sortMediaAttachments(medias));

/** Like toGalleryEntries, but keeps `medias` in the order given. */
export const toOrderedGalleryEntries = (
  medias: readonly PostMediaTileMedia[],
): GalleryEntry[] =>
  medias.flatMap((attachment): GalleryEntry[] => {
    if (attachment.media) {
      const url = getMediaUrl(attachment.media);
      if (getLinkType(url) === LinkType.PDF) {
        const item: MediaGalleryItem = attachment.media.thumbnailImageUrl
          ? { url: attachment.media.thumbnailImageUrl, type: "image" }
          : { url, type: "pdf" };
        return [{ item, attachmentId: attachment.id, pdfUrl: url }];
      }
      return [
        {
          item: {
            url,
            type: isVideoMedia(attachment.media) ? "video" : "image",
          },
          attachmentId: attachment.id,
        },
      ];
    }
    if (attachment.libraryItem?.coverImage) {
      const url = getCoverImageUrl(attachment.libraryItem.coverImage);
      if (url)
        return [
          {
            item: { url, type: "image" },
            attachmentId: attachment.id,
            title: attachment.libraryItem.name.en,
            libraryItemId: attachment.libraryItem.id,
          },
        ];
    }
    return [];
  });

/** False for resources with no cover image, which the gallery can't show. */
export const hasGallerySlide = (attachment: PostMediaTileMedia): boolean =>
  toOrderedGalleryEntries([attachment]).length > 0;

/**
 * The attachment's slide in a gallery of `medias` (e.g. PostDetailDialog's),
 * or undefined when it has none.
 */
export const gallerySlideIndex = (
  medias: readonly PostMediaTileMedia[],
  attachment: PostMediaTileMedia,
): number | undefined => {
  const index = toGalleryEntries(medias).findIndex(
    (entry) => entry.attachmentId === attachment.id,
  );
  return index === -1 ? undefined : index;
};

export const toGalleryItems = (
  medias: readonly PostMediaTileMedia[],
): MediaGalleryItem[] => toGalleryEntries(medias).map((entry) => entry.item);

export const toMediaTiles = (
  medias: readonly PostMediaTileMedia[],
  onTileClick?: (index: number, attachment: PostMediaTileMedia) => void,
): ReactElement[] =>
  sortMediaAttachments(medias).map((attachment, index) => (
    <PostMediaTile
      key={attachment.id}
      attachment={attachment}
      onClick={onTileClick ? () => onTileClick(index, attachment) : undefined}
    />
  ));
